import * as Sentry from '@sentry/browser';

/* Error reporting to Sentry (project budovan-twin). The DSN and the env name come from /config.json, so
 * one build serves every env; without a DSN (local development) nothing is sent. Reports carry the
 * building and how the viewer runs (in the app's WebView or as a page); main.ts takes a test token out
 * of the URL before this starts, so no report carries it. */

/** What failed, as the `error_type` tag: one Sentry search per kind of failure. */
export const ERROR_TYPE = {
  /** The building's manifest or model did not load: the viewer showed its failure text. */
  load: 'load_failed',
} as const;
type ErrorType = (typeof ERROR_TYPE)[keyof typeof ERROR_TYPE];

const TAG = { errorType: 'error_type', building: 'building', inApp: 'in_app', mode: 'mode' } as const;
type Tag = Exclude<keyof typeof TAG, 'errorType'>;

export interface ErrorReportingConfig {
  sentryDsn?: string;
  sentryEnvironment?: string;
  apiUrl: string;
}

let enabled = false;

export function initErrorReporting(config: ErrorReportingConfig): void {
  if (enabled || !config.sentryDsn) return;
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment ?? 'local',
    tracesSampleRate: 0.2,
    // Profiles every traced page load and navigation of a sampled session; the page must be served with
    // `Document-Policy: js-profiling` (infra/demo/web/default.conf.template) or the browser refuses.
    profileSessionSampleRate: 0.2,
    profileLifecycle: 'trace',
    tracePropagationTargets: [config.apiUrl],
    // No request bodies (sign-in passwords), cookies, query strings or IP addresses; the user is set by id.
    dataCollection: { userInfo: false, cookies: false, httpBodies: [], urlQueryParams: false },
    integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
  });
  enabled = true;
}

/** Context every later report carries. */
export function setSentryTags(tags: Partial<Record<Tag, string>>): void {
  if (!enabled) return;
  for (const [key, value] of Object.entries(tags)) Sentry.setTag(TAG[key as Tag], value);
}

/** A failure the viewer caught and showed, so the SDK would not see it. */
export function reportError(error: unknown, type: ErrorType): void {
  if (enabled) Sentry.captureException(error, { tags: { [TAG.errorType]: type } });
}
